<template>
  <div @click="handleClick">
    <div class="masterclass-card">
      <div class="card-content">
        <div
          class="card-image"
          :style="{ backgroundImage: `url(${thumbnail})` }"
        >
          <div class="card-overlay"></div>
        </div>

        <div class="card-info">
          <h2 class="card-title">{{ title }}</h2>
          <p class="card-description">
            {{ truncatedBlurb }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from "vue";

const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  thumbnail: {
    type: String,
    required: true,
  },
  blurb: {
    type: String,
    default: "",
  },
  assigned: {
    type: [String, Number],
    default: 0,
  },
  completed: {
    type: [String, Number],
    default: 0,
  },
  maxLength: {
    type: Number,
    default: 80,
  },
});

const emit = defineEmits(["card-click"]);

const handleClick = () => {
  emit("card-click", props.id);
};

const truncatedBlurb = computed(() => {
  if (props.blurb.length <= props.maxLength) return props.blurb;
  return props.blurb.slice(0, props.maxLength) + "...";
});
</script>

<style scoped>
.masterclass-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  height: 300px;
  position: relative;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.masterclass-card:hover {
  transform: translateY(-5px);
}

.card-content {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(to right, #36d1dc, #5b86e5);
}

.card-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  z-index: 1;
}

.card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.6);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 2;
}

.masterclass-card:hover .card-overlay {
  opacity: 1;
}

.card-info {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 1.5rem;
  color: white;
  height: 200px;
  z-index: 3;
  transform: translateY(calc(100% - 90px));
  transition: transform 0.4s ease;
  background-color: #f2f3fb;
}

.card-info::after {
  content: "";
  position: absolute;
  top: -25px;
  left: -25px;
  right: -25px;
  height: 20%;
  background-color: #f2f3fb;
  opacity: 0;
  filter: blur(8px);
  z-index: 5;
}

.masterclass-card:hover .card-info {
  transform: translateY(0px);
}

.card-info:hover::after {
  opacity: 1;
}

.card-title {
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  word-break: break-word;
  color: var(--primary-blue);
}

.card-description {
  font-size: 0.9rem;
  line-height: 1.4;
  margin-bottom: 1rem;
  opacity: 0;
  transform: translateY(20px);
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  transition-delay: 0.1s;
  word-break: break-word;
  color: var(--primary-blue);
}

.masterclass-card:hover .card-description {
  opacity: 1;
  transform: translateY(0);
}
</style>
